import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../services/api";
import { getMyPurchases } from "../services/purchase";
import { 
  PlayCircle, 
  ArrowRight,
  ArrowLeft,
  Search,
  Filter,
  Star,
  Users,
  Award
} from "lucide-react";
import { motion } from "framer-motion";
import toast, { Toaster } from 'react-hot-toast';
import PublicNavbar from "../components/PublicNavbar";

export default function PublicCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasedIds, setPurchasedIds] = useState([]);

  useEffect(() => {
    if (localStorage.getItem("token")) {
      getMyPurchases().then(res => setPurchasedIds(res.data.map(c => String(c.id)))).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await api.get("/courses");
        setCourses(res.data);
      } catch (err) {
        console.error("Error fetching courses:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      
      <PublicNavbar />

      <main className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
          <div>
            <h2 className="text-3xl font-black text-slate-900 mb-2">Learn from the Best</h2>
            <p className="text-slate-500 font-medium">Expert-led courses to accelerate your career growth</p>
          </div>
          
          <div className="flex gap-3">
             <div className="relative">
               <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
               <input 
                 type="text" 
                 placeholder="Search courses..." 
                 className="pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-600/10 transition-all w-64"
               />
             </div>
             <button className="p-3 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-indigo-600">
               <Filter size={20} />
             </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {courses.map((course, i) => (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                key={i} 
                className="group bg-white rounded-[2.5rem] border border-slate-100 overflow-hidden hover:shadow-2xl transition-all"
              >
                <div className="aspect-video bg-slate-100 relative overflow-hidden">
                  {course.thumbnail ? (
                    <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <PlayCircle size={64} />
                    </div>
                  )}
                  <div className="absolute top-6 left-6 px-4 py-2 bg-white/90 backdrop-blur rounded-2xl text-[10px] font-black text-indigo-600 uppercase tracking-widest shadow-xl">
                    {course.category || "General"}
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="text-xl font-black text-slate-900 mb-4 line-clamp-2 min-h-[3.5rem] group-hover:text-indigo-600 transition-colors">
                    {course.title}
                  </h3>
                  <div className="flex items-center gap-2 text-amber-500 mb-8">
                    <Star size={16} fill="currentColor" />
                    <span className="text-sm font-black text-slate-900">4.9</span>
                    <span className="text-xs font-bold text-slate-400 tracking-tighter">(2k+ Enrolled)</span>
                  </div>
                  <div className="flex items-center justify-between pt-6 border-t border-slate-50">
                    <p className="text-2xl font-black text-slate-900">₹{course.price}</p>
                    <button 
                      onClick={() => {
                        if (localStorage.getItem("token")) {
                          if (purchasedIds.includes(String(course.id))) {
                            navigate(`/course/${course.id}`);
                          } else {
                            navigate(`/course-detail/${course.id}`);
                          }
                        } else {
                          navigate("/login");
                        }
                      }}
                      className="px-6 py-3 bg-indigo-600 text-white font-black rounded-xl flex items-center gap-2 hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                    >
                      {purchasedIds.includes(String(course.id)) ? "View Course" : "Enroll Now"} <ArrowRight size={20} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
